import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

interface Stats {
  currentWeight: number | null;
  weightDelta: number | null;
  sessionsThisWeek: number;
  streakWeeks: number;
  currentChest: number | null;
  chestDelta: number | null;
  currentWaist: number | null;
  waistDelta: number | null;
}

function isoWeekStart(d: Date): Date {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // lunes = 0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function useDashboardStats() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    (async () => {
      const { data: measurements } = await supabase
        .from("body_measurements")
        .select("date, weight_kg, chest_cm, waist_cm")
        .order("date", { ascending: true });

      const withWeight = (measurements ?? []).filter((m) => m.weight_kg != null);
      const currentWeight = withWeight.at(-1)?.weight_kg ?? null;
      const firstWeight = withWeight.at(0)?.weight_kg ?? null;
      const weightDelta = currentWeight != null && firstWeight != null ? currentWeight - firstWeight : null;

      const withChest = (measurements ?? []).filter((m) => m.chest_cm != null);
      const currentChest = withChest.at(-1)?.chest_cm ?? null;
      const firstChest = withChest.at(0)?.chest_cm ?? null;
      const chestDelta = currentChest != null && firstChest != null ? currentChest - firstChest : null;

      const withWaist = (measurements ?? []).filter((m) => m.waist_cm != null);
      const currentWaist = withWaist.at(-1)?.waist_cm ?? null;
      const firstWaist = withWaist.at(0)?.waist_cm ?? null;
      const waistDelta = currentWaist != null && firstWaist != null ? currentWaist - firstWaist : null;

      const { data: sessions } = await supabase
        .from("workout_sessions")
        .select("date")
        .order("date", { ascending: false });

      const weekStart = isoWeekStart(new Date());
      const sessionsThisWeek = (sessions ?? []).filter((s) => new Date(s.date) >= weekStart).length;

      // Racha: semanas consecutivas (hacia atrás desde hoy) con al menos una sesión registrada.
      const sessionWeeks = new Set(
        (sessions ?? []).map((s) => isoWeekStart(new Date(s.date)).toISOString().slice(0, 10))
      );
      let streakWeeks = 0;
      const cursor = isoWeekStart(new Date());
      while (sessionWeeks.has(cursor.toISOString().slice(0, 10))) {
        streakWeeks += 1;
        cursor.setDate(cursor.getDate() - 7);
      }

      setStats({
        currentWeight,
        weightDelta,
        sessionsThisWeek,
        streakWeeks,
        currentChest,
        chestDelta,
        currentWaist,
        waistDelta,
      });
    })();
  }, []);

  return stats;
}
